import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CategoryManagementPageModel } from '../pageModels/CategoryManagementPageModel';
import { Category, HomeShowItem, CategoryTreeItem } from '../../models/CategoryManagement';
import { AliOssUtil } from '../../utils/AliOssUtil';
import { OssScene } from '../../global/OssConfig';
import Loading from '../../components/Loading/Loading';
import { globalStore } from '../../models/globalStore';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const CategoryManagement: React.FC = () => {
    const [model] = useState(() => new CategoryManagementPageModel());
    const [activeTab, setActiveTab] = useState<'list' | 'home'>('list');
    const [editForm, setEditForm] = useState<Partial<Category>>({});
    const [selectedFirstCategory, setSelectedFirstCategory] = useState<CategoryTreeItem | null>(null);
    const [newCategoryForm, setNewCategoryForm] = useState<Partial<Category>>({});

    useEffect(() => {
        globalStore.selectedMenuKey = 'category-management';
        model.loadCategories();
        model.loadHomeShows();
        // 默认选择第一个一级分类
        if (model.categoryTree.length > 0) {
            setSelectedFirstCategory(model.categoryTree[0]);
        }
        // 设置默认父分类ID
        setNewCategoryForm(prev => ({ ...prev, parentId: 0 }));
    }, [model]);

    useEffect(() => {
        // 当分类树更新时，重新选择第一个一级分类
        if (model.categoryTree.length > 0 && !selectedFirstCategory) {
            setSelectedFirstCategory(model.categoryTree[0]);
        }
    }, [model.categoryTree, selectedFirstCategory]);

    // 添加全局粘贴事件监听器
    useEffect(() => {
        const handleGlobalPaste = async (e: ClipboardEvent) => {
            console.log('全局粘贴事件触发', {
                showEditModal: model.showEditModal,
                showAddCategoryModal: model.showAddCategoryModal,
                editingCategory: model.editingCategory,
                newCategoryFormParentId: newCategoryForm.parentId
            });
            
            // 检查是否有图片数据
            if (e.clipboardData && e.clipboardData.items) {
                for (let i = 0; i < e.clipboardData.items.length; i++) {
                    const item = e.clipboardData.items[i];
                    if (item.type.indexOf('image') !== -1) {
                        console.log('检测到图片数据，类型:', item.type);
                        e.preventDefault();
                        const file = item.getAsFile();
                        if (file) {
                            console.log('获取到文件:', file.name, file.size);
                            // 智能判断应该添加到哪个表单
                            if (model.showEditModal && model.editingCategory?.parentId !== 0) {
                                console.log('添加到编辑表单');
                                // 如果编辑模态框打开且是二级分类，添加到编辑表单
                                try {
                                    const url = await AliOssUtil.uploadFile(file, OssScene.category);
                                    setEditForm(prev => ({ ...prev, iconUrl: url }));
                                    console.log('编辑表单图片更新成功:', url);
                                } catch (error) {
                                    console.error('粘贴图片上传失败:', error);
                                }
                            } else if (model.showAddCategoryModal && newCategoryForm.parentId !== 0) {
                                console.log('添加到新建表单');
                                // 如果添加分类模态框打开且是二级分类，添加到新建表单
                                try {
                                    const url = await AliOssUtil.uploadFile(file, OssScene.category);
                                    setNewCategoryForm(prev => ({ ...prev, iconUrl: url }));
                                    console.log('新建表单图片更新成功:', url);
                                } catch (error) {
                                    console.error('粘贴图片上传失败:', error);
                                }
                            } else {
                                console.log('不满足粘贴条件:', {
                                    showEditModal: model.showEditModal,
                                    editingCategoryParentId: model.editingCategory?.parentId,
                                    showAddCategoryModal: model.showAddCategoryModal,
                                    newCategoryFormParentId: newCategoryForm.parentId
                                });
                            }
                        }
                        break;
                    }
                }
            } else {
                console.log('剪贴板中没有图片数据');
            }
        };

        // 添加键盘快捷键支持
        const handleKeyDown = (e: KeyboardEvent) => {
            // 检查是否是Ctrl+V (Windows) 或 Cmd+V (Mac)
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                console.log('检测到Ctrl+V/Cmd+V快捷键');
                // 触发粘贴事件，让上面的handleGlobalPaste处理
                // 这里不需要额外处理，因为浏览器会自动触发paste事件
            }
        };

        // 添加全局粘贴事件监听器
        document.addEventListener('paste', handleGlobalPaste);
        // 添加键盘事件监听器
        document.addEventListener('keydown', handleKeyDown);

        // 清理函数
        return () => {
            document.removeEventListener('paste', handleGlobalPaste);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [model.showEditModal, model.editingCategory, model.showAddCategoryModal, newCategoryForm.parentId]);

    const handleEditCategory = (category: Category) => {
        setEditForm({ ...category });
        model.setEditingCategory(category);
    };

    const handleSaveCategory = async () => {
        if (model.editingCategory) {
            await model.updateCategory(editForm);
        }
    };

    const handleAddCategory = async () => {
        if (newCategoryForm.name && newCategoryForm.keywords) {
            await model.addCategory({
                ...newCategoryForm,
                parentId: selectedFirstCategory ? selectedFirstCategory.id : (newCategoryForm.parentId || 0),
                iconUrl: selectedFirstCategory ? (newCategoryForm.iconUrl || '') : ''
            });
            setNewCategoryForm({ parentId: 0 });
        }
    };

    const handleHideMainCategory = async (categoryId: number) => {
        if (window.confirm('确定要隐藏此一级分类吗？')) {
            await model.hideMainCategory(categoryId);
            // 如果隐藏的是当前选中的分类，选择第一个分类
            if (selectedFirstCategory?.id === categoryId) {
                const remainingCategories = model.categoryTree.filter(cat => cat.id !== categoryId);
                if (remainingCategories.length > 0) {
                    setSelectedFirstCategory(remainingCategories[0]);
                } else {
                    setSelectedFirstCategory(null);
                }
            }
        }
    };

    const handleDeleteMainCategory = async (category: CategoryTreeItem) => {
        const childCount = category.children?.length ?? 0;
        const message = childCount > 0
            ? `确定删除一级类目「${category.name}」？\n将同时删除其下 ${childCount} 个二级类目及数据库中间层级，不可恢复。`
            : `确定删除一级类目「${category.name}」？\n将同时删除数据库中的关联中间层级，不可恢复。`;
        if (!window.confirm(message)) {
            return;
        }
        const success = await model.deleteMainCategory(category.id);
        if (success && selectedFirstCategory?.id === category.id) {
            const remainingCategories = model.categoryTree.filter(cat => cat.id !== category.id);
            if (remainingCategories.length > 0) {
                setSelectedFirstCategory(remainingCategories[0]);
            } else {
                setSelectedFirstCategory(null);
            }
        }
    };

    const handleDeleteSecondCategory = async (categoryId: number) => {
        if (window.confirm('确定要删除此二级分类吗？')) {
            await model.deleteSecondCategory(categoryId);
        }
    };

    const handleImageUpload = async (file: File) => {
        try {
            const url = await AliOssUtil.uploadFile(file, OssScene.category);
            if (model.showEditModal && model.editingCategory?.parentId !== 0) {
                setEditForm(prev => ({ ...prev, iconUrl: url }));
            } else if (model.showAddCategoryModal && newCategoryForm.parentId !== 0) {
                setNewCategoryForm(prev => ({ ...prev, iconUrl: url }));
            }
        } catch (error) {
            console.error('上传失败:', error);
        }
    };

    const handlePasteImage = async () => {
        try {
            const url = await AliOssUtil.uploadImageFromClipboard(OssScene.category);
            if (model.showEditModal && model.editingCategory?.parentId !== 0) {
                setEditForm(prev => ({ ...prev, iconUrl: url }));
            } else if (model.showAddCategoryModal && newCategoryForm.parentId !== 0) {
                setNewCategoryForm(prev => ({ ...prev, iconUrl: url }));
            }
        } catch (error) {
            console.error('粘贴失败:', error);
        }
    };

    const handleDropImage = (e: React.DragEvent) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleImageUpload(files[0]);
        }
    };

    const handleSetHomeShow = (index: number) => {
        model.setSelectedIndex(index);
        model.setShowHomeShowModal(true);
    };

    const handleConfirmHomeShow = async () => {
        await model.setHomeShow();
    };

    const renderCategoryList = () => (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* 一级分类Tab */}
            <div style={{
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: 'white',
                padding: '0 20px'
            }}>
                <div style={{ 
                    display: 'flex', 
                    overflowX: 'auto',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex' }}>
                        {model.categoryTree.map(category => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedFirstCategory(category)}
                                style={{
                                    padding: '15px 30px',
                                    backgroundColor: 'transparent',
                                    color: selectedFirstCategory?.id === category.id ? '#1890ff' : '#333',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    borderBottom: selectedFirstCategory?.id === category.id ? '2px solid #1890ff' : 'none',
                                    fontWeight: selectedFirstCategory?.id === category.id ? '500' : 'normal'
                                }}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => {
                            setNewCategoryForm({ parentId: 0 });
                            setSelectedFirstCategory(null);
                            model.setShowAddCategoryModal(true);
                        }}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#52c41a',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        创建一级类目
                    </button>
                </div>
            </div>

            {/* 二级分类列表 */}
            <div style={{ flex: 1, padding: '20px', overflow: 'auto' }}>
                {selectedFirstCategory && (
                    <>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: '20px' 
                        }}>
                            <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
                                {selectedFirstCategory.name} - 二级分类
                            </h2>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => {
                                        setNewCategoryForm({ parentId: selectedFirstCategory?.id || 0 });
                                        model.setShowAddCategoryModal(true);
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#52c41a',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    添加二级类目
                                </button>
                                <button
                                    onClick={() => handleEditCategory(selectedFirstCategory)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#1890ff',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    编辑
                                </button>
                                <button
                                    onClick={() => handleHideMainCategory(selectedFirstCategory.id)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#faad14',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    隐藏此一级类目
                                </button>
                                <button
                                    onClick={() => handleDeleteMainCategory(selectedFirstCategory)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#ff4d4f',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    删除此一级类目
                                </button>
                            </div>
                        </div>
                        
                        {selectedFirstCategory.children && selectedFirstCategory.children.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                                {selectedFirstCategory.children.map(child => (
                                                                    <div key={child.id} style={{ 
                                    border: '1px solid #e0e0e0',
                                    padding: '15px',
                                    backgroundColor: 'white',
                                    position: 'relative'
                                }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                            <img 
                                                src={child.iconUrl} 
                                                alt={child.name}
                                                style={{ width: '40px', height: '40px', marginRight: '15px', objectFit: 'cover', borderRadius: '4px' }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '16px', color: '#333', marginBottom: '5px' }}>
                                                    {child.name}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#666' }}>
                                                    关键词: {child.keywords}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <EditOutlined
                                                    onClick={() => handleEditCategory(child)}
                                                    style={{
                                                        fontSize: '18px',
                                                        color: '#1890ff',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <DeleteOutlined
                                                    onClick={() => handleDeleteSecondCategory(child.id)}
                                                    style={{
                                                        fontSize: '18px',
                                                        color: '#ff4d4f',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '40px', 
                                color: '#999',
                                fontSize: '14px'
                            }}>
                                该分类下暂无二级分类
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    const renderHomeShowSettings = () => (
        <div style={{ padding: '20px' }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>首页展示设置</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '15px' }}>
                {Array.from({ length: 20 }, (_, index) => {
                    const homeShow = model.homeShows.find(hs => hs.index === index);
                    return (
                        <div key={index} style={{
                            width: '100%',
                            height: '120px',
                            border: '1px solid #e0e0e0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            backgroundColor: homeShow ? '#f0f8ff' : '#f9f9f9'
                        }} onClick={() => handleSetHomeShow(index)}>
                            {homeShow ? (
                                <>
                                    <img 
                                        src={homeShow.secondCategory.iconUrl} 
                                        alt={homeShow.secondCategory.name}
                                        style={{ width: '40px', height: '40px', marginBottom: '8px', objectFit: 'cover' }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#333', textAlign: 'center' }}>
                                        {homeShow.secondCategory.name}
                                    </div>
                                </>
                            ) : (
                                <PlusOutlined style={{ fontSize: '24px', color: '#ccc' }} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderEditModal = () => (
        model.showEditModal && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '30px',
                    width: '500px',
                    maxWidth: '90vw'
                }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333' }}>
                        {model.editingCategory?.parentId === 0 ? '编辑一级分类' : '编辑二级分类'}
                    </h3>
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                            分类名称
                        </label>
                        <input
                            type="text"
                            value={editForm.name || ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d9d9d9',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    {/* 只有二级分类才显示图标上传 */}
                    {model.editingCategory?.parentId !== 0 && (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                                图标
                            </label>
                            <div style={{
                                border: '2px dashed #d9d9d9',
                                padding: '20px',
                                textAlign: 'center',
                                marginBottom: '10px'
                            }} onDrop={handleDropImage} onDragOver={(e) => e.preventDefault()}>
                                {editForm.iconUrl ? (
                                    <img 
                                        src={editForm.iconUrl} 
                                        alt="分类图标"
                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div style={{ color: '#999' }}>
                                        <div>拖拽图片到此处或点击上传</div>
                                        <div style={{ fontSize: '12px', marginTop: '5px', color: '#ccc' }}>
                                            支持 Ctrl+V / Cmd+V 粘贴图片
                                        </div>
                                    </div>
                                )}
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                                style={{ marginBottom: '10px' }}
                            />
                            <button
                                onClick={handlePasteImage}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#52c41a',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                                title="或使用 Ctrl+V / Cmd+V 快捷键"
                            >
                                粘贴图片
                            </button>
                        </div>
                    )}

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                            关键词
                        </label>
                        <input
                            type="text"
                            value={editForm.keywords || ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, keywords: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d9d9d9',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button
                            onClick={() => model.setEditingCategory(null)}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#f5f5f5',
                                color: '#333',
                                border: '1px solid #d9d9d9',
                                cursor: 'pointer'
                            }}
                        >
                            取消
                        </button>
                        <button
                            onClick={handleSaveCategory}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#1890ff',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            保存
                        </button>
                    </div>
                </div>
            </div>
        )
    );

    const renderAddCategoryModal = () => (
        model.showAddCategoryModal && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '30px',
                    width: '500px',
                    maxWidth: '90vw'
                }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333' }}>
                        {newCategoryForm.parentId === 0 ? '创建一级类目' : '添加二级类目'}
                    </h3>

                    {/* 只有创建一级分类时才显示父分类选择 */}
                    {!selectedFirstCategory && (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                                父分类
                            </label>
                            <select
                                value={newCategoryForm.parentId || 0}
                                onChange={(e) => setNewCategoryForm(prev => ({ ...prev, parentId: Number(e.target.value) }))}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #d9d9d9',
                                    fontSize: '14px'
                                }}
                            >
                                <option value={0}>无父分类（一级分类）</option>
                                {model.categoryTree.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                            分类名称
                        </label>
                        <input
                            type="text"
                            value={newCategoryForm.name || ''}
                            onChange={(e) => setNewCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d9d9d9',
                                fontSize: '14px'
                            }}
                            placeholder="请输入分类名称"
                        />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                            关键词
                        </label>
                        <input
                            type="text"
                            value={newCategoryForm.keywords || ''}
                            onChange={(e) => setNewCategoryForm(prev => ({ ...prev, keywords: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d9d9d9',
                                fontSize: '14px'
                            }}
                            placeholder="请输入关键词"
                        />
                    </div>

                    {/* 只有创建二级分类时才显示图标上传 */}
                    {newCategoryForm.parentId !== 0 && (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                                图标
                            </label>
                            <div style={{
                                border: '2px dashed #d9d9d9',
                                padding: '20px',
                                textAlign: 'center',
                                marginBottom: '10px'
                            }} onDrop={handleDropImage} onDragOver={(e) => e.preventDefault()}>
                                {newCategoryForm.iconUrl ? (
                                    <img 
                                        src={newCategoryForm.iconUrl} 
                                        alt="分类图标"
                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div style={{ color: '#999' }}>
                                        <div>拖拽图片到此处或点击上传</div>
                                        <div style={{ fontSize: '12px', marginTop: '5px', color: '#ccc' }}>
                                            支持 Ctrl+V / Cmd+V 粘贴图片
                                        </div>
                                    </div>
                                )}
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                                style={{ marginBottom: '10px' }}
                            />
                            <button
                                onClick={handlePasteImage}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#52c41a',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                                title="或使用 Ctrl+V / Cmd+V 快捷键"
                            >
                                粘贴图片
                            </button>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button
                            onClick={() => {
                                model.setShowAddCategoryModal(false);
                                setNewCategoryForm({ parentId: 0 });
                            }}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#f5f5f5',
                                color: '#333',
                                border: '1px solid #d9d9d9',
                                cursor: 'pointer'
                            }}
                        >
                            取消
                        </button>
                        <button
                            onClick={handleAddCategory}
                            disabled={!newCategoryForm.name || !newCategoryForm.keywords}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: (!newCategoryForm.name || !newCategoryForm.keywords) ? '#f5f5f5' : '#1890ff',
                                color: (!newCategoryForm.name || !newCategoryForm.keywords) ? '#999' : 'white',
                                border: 'none',
                                cursor: (!newCategoryForm.name || !newCategoryForm.keywords) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {newCategoryForm.parentId === 0 ? '创建' : '添加'}
                        </button>
                    </div>
                </div>
            </div>
        )
    );

    const renderHomeShowModal = () => (
        model.showHomeShowModal && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '30px',
                    width: '1000px',
                    maxWidth: '95vw',
                    maxHeight: '85vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>
                        设置第{model.selectedIndex + 1}个位置 - 选择分类
                    </h3>
                    
                    {/* 分类选择区域 */}
                    <div style={{ 
                        flex: 1, 
                        overflow: 'auto', 
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        padding: '20px'
                    }}>
                        {model.categoryTree.map((firstCategory, index) => (
                            <div key={firstCategory.id} style={{ 
                                marginBottom: index < model.categoryTree.length - 1 ? '16px' : '0',
                                paddingBottom: index < model.categoryTree.length - 1 ? '16px' : '0',
                                borderBottom: index < model.categoryTree.length - 1 ? '1px solid #e8e8e8' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '40px'
                            }}>
                                {/* 一级类目名称 - 左侧固定宽度 */}
                                <div style={{
                                    width: '120px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#333',
                                    padding: '15px 0',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '60px'
                                }}>
                                    {firstCategory.name}
                                </div>
                                
                                {/* 二级类目水平排列 - 右侧自适应 */}
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    alignItems: 'center',
                                    marginLeft: '8px',
                                    minHeight: '40px'
                                }}>
                                    {firstCategory.children && firstCategory.children.map(secondCategory => (
                                        <div key={secondCategory.id} style={{
                                            height: '30px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            backgroundColor: model.selectedSecondCategoryId === secondCategory.id ? '#e6f7ff' : 'white',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0 10px',
                                            minWidth: 'fit-content',
                                            maxWidth: '180px'
                                        }} onClick={() => model.setSelectedSecondCategoryId(secondCategory.id)}>
                                            
                                            {/* 分类图标 */}
                                            <img 
                                                src={secondCategory.iconUrl} 
                                                alt={secondCategory.name}
                                                style={{ 
                                                    width: '18px', 
                                                    height: '18px', 
                                                    marginRight: '6px', 
                                                    objectFit: 'cover',
                                                    borderRadius: '2px',
                                                    flexShrink: 0
                                                }}
                                            />
                                            
                                            {/* 分类名称 */}
                                            <div style={{ 
                                                fontSize: '12px', 
                                                color: '#333',
                                                fontWeight: '500',
                                                marginRight: '6px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1
                                            }}>
                                                {secondCategory.name}
                                            </div>
                                            
                                            {/* 选择按钮 */}
                                            <div style={{
                                                width: '14px',
                                                height: '14px',
                                                borderRadius: '50%',
                                                border: '1.5px solid #d9d9d9',
                                                backgroundColor: model.selectedSecondCategoryId === secondCategory.id ? '#1890ff' : 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                {model.selectedSecondCategoryId === secondCategory.id && (
                                                    <div style={{
                                                        width: '5px',
                                                        height: '5px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white'
                                                    }} />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* 如果没有二级分类 */}
                                    {(!firstCategory.children || firstCategory.children.length === 0) && (
                                        <div style={{
                                            padding: '6px 16px',
                                            color: '#999',
                                            fontSize: '12px',
                                            backgroundColor: '#f9f9f9',
                                            borderRadius: '4px',
                                            height: '30px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            该分类下暂无二级分类
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 底部按钮区域 */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: '10px',
                        marginTop: '20px',
                        paddingTop: '20px',
                        borderTop: '1px solid #e0e0e0'
                    }}>
                        <button
                            onClick={() => model.setShowHomeShowModal(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#f5f5f5',
                                color: '#333',
                                border: '1px solid #d9d9d9',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        >
                            取消
                        </button>
                        <button
                            onClick={handleConfirmHomeShow}
                            disabled={model.selectedSecondCategoryId === 0}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: model.selectedSecondCategoryId === 0 ? '#f5f5f5' : '#1890ff',
                                color: model.selectedSecondCategoryId === 0 ? '#999' : 'white',
                                border: 'none',
                                cursor: model.selectedSecondCategoryId === 0 ? 'not-allowed' : 'pointer',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        >
                            确定
                        </button>
                    </div>
                </div>
            </div>
        )
    );

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: 'white'
            }}>
                <div style={{ display: 'flex' }}>
                    <button
                        onClick={() => setActiveTab('list')}
                        style={{
                            padding: '15px 30px',
                            backgroundColor: 'transparent',
                            color: activeTab === 'list' ? '#1890ff' : '#333',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            borderBottom: activeTab === 'list' ? '2px solid #1890ff' : 'none',
                            fontWeight: activeTab === 'list' ? '500' : 'normal'
                        }}
                    >
                        分类列表
                    </button>
                    <button
                        onClick={() => setActiveTab('home')}
                        style={{
                            padding: '15px 30px',
                            backgroundColor: 'transparent',
                            color: activeTab === 'home' ? '#1890ff' : '#333',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            borderBottom: activeTab === 'home' ? '2px solid #1890ff' : 'none',
                            fontWeight: activeTab === 'home' ? '500' : 'normal'
                        }}
                    >
                        首页展示设置
                    </button>
                </div>
            </div>

            <div style={{ flex: 1, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                {activeTab === 'list' ? renderCategoryList() : renderHomeShowSettings()}
            </div>

            {model.loading && <Loading />}

            {renderEditModal()}
            {renderAddCategoryModal()}
            {renderHomeShowModal()}
        </div>
    );
};

export default observer(CategoryManagement); 